import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext.jsx';
import { HUB_ROOM_KEYS } from '../lib/access.rooms.generated.js';

// The client's HIDE layer for Hub rooms. It never evaluates grants itself — that
// would make the browser an authority. It reads the server-resolved set from
// GET /api/access/me and hides anything whose room symbol isn't in it.
//
// Hiding is UX only. The security boundary is the backend 403 on each gated
// handler, which stays in place so a deep-linked route still denies.
//
// Fetched once, after identity resolves — not polled. Same reasoning as
// AuthContext: a polling loop against an endpoint backed by Snowflake is what made
// the shared warehouse ~60% of the IHS bill. The backend already caches the
// ACCESS_MAP read for 60s, so a grant change lands on the next full page load.
//
// Rooms are issued by the Hub. Empty grants hide staff surfaces.

// `staff` is the server's answer to "which desks does this person handle?" - see
// staffAccess.js. Null until it is known, and null again on any failure.
const AccessContext = createContext({
  rooms: new Set(),
  staff: null,
  status: 'loading', // 'loading' | 'ready' | 'anonymous' | 'error'
});

export function AccessProvider({ children }) {
  const { status: authStatus } = useAuth();
  const [state, setState] = useState({ rooms: new Set(), staff: null, status: 'loading' });

  useEffect(() => {
    if (authStatus === 'loading') return;
    if (authStatus !== 'authenticated') {
      // No identity, no rooms. Fail closed rather than leaving it 'loading'.
      setState({ rooms: new Set(), staff: null, status: 'anonymous' });
      return;
    }

    let cancelled = false;

    fetch('/api/access/me')
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then((body) => {
        if (cancelled) return;
        const rooms = Array.isArray(body?.rooms) ? body.rooms : [];
        const staff = body?.staff && typeof body.staff === 'object' ? body.staff : null;
        setState({ rooms: new Set(rooms), staff, status: 'ready' });
      })
      .catch(() => {
        // Fail closed: an empty set hides everything gated rather than leaking it.
        if (!cancelled) setState({ rooms: new Set(), staff: null, status: 'error' });
      });

    return () => {
      cancelled = true;
    };
  }, [authStatus]);

  return <AccessContext.Provider value={state}>{children}</AccessContext.Provider>;
}

export function useAccess() {
  const { rooms, staff, status } = useContext(AccessContext);

  // Mirrors the backend guard in accessMap.js: a key the Hub never issued is a
  // typo or a stale symbol, and must fail LOUDLY here rather than silently
  // hiding — a silent hide on a bad key is the `recap`/`recap-reporting` bug
  // wearing a different hat.
  const hasRoom = useMemo(() => {
    const valid = new Set(Object.values(HUB_ROOM_KEYS));
    return (roomKey) => {
      if (!valid.has(roomKey)) {
        throw new Error(`unknown room key: ${JSON.stringify(roomKey)} — not issued by the Hub`);
      }
      return rooms.has(roomKey);
    };
  }, [rooms]);

  return { rooms, staff, status, hasRoom, loading: status === 'loading' };
}

/**
 * Route/section gate. Renders `children` only for a member of `room`.
 *
 * Pass the generated SYMBOL, never a literal: <RequireRoom room={IT_STAFF}>.
 * The drift checker scans for raw strings in `hasRoom(...)` calls, and an
 * unissued key throws from `hasRoom` above.
 */
export function RequireRoom({ room, children, fallback = null }) {
  const { hasRoom, loading } = useAccess();

  // Hide while the room set is in flight so a gated section never flashes.
  if (loading) return null;
  if (hasRoom(room)) return children;
  return fallback ?? <AccessRequired />;
}

function AccessRequired() {
  return (
    <>
      <section className="hero">
        <h1 className="hero__title">Access required</h1>
        <p className="hero__sub">You don&rsquo;t have access to this part of IT Help.</p>
      </section>
      <div className="card card--pad">
        <div className="section__title">Need it?</div>
        <p className="tile__body">
          Access is granted by security group in the IHS Hub, not in this app. Ask IT to add
          you to the right group and it will apply the next time you load the page.
        </p>
      </div>
    </>
  );
}
